import React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, TooltipProps } from 'recharts'
import { NumberType } from '@api'
import { prettyNumber } from '@ui'

const noData = [
  { name: 'a', dataKeys: ['value'], value: 10 },
  { name: 'b', dataKeys: ['value'], value: 8 },
  { name: 'c', dataKeys: ['value'], value: 6 },
  { name: 'd', dataKeys: ['value'], value: 4 },
  { name: 'e', dataKeys: ['value'], value: 2 },
]

const getMaxLabelLength = (items = [], key: string) => {
  let l = 0

  items.forEach((item: any) => {
    const value = item[key] || ''
    if (value.length > l) {
      l = value.length
    }
  })

  return l
}

interface IStackedBarProps {
  data: any
  layout: 'vertical' | 'horizontal'
  width: number
  height: number
  xdata: { key?: string; label?: string; groups: string[] }
  ydata: { key?: string; label?: string }
  tooltip: (props: TooltipProps) => React.ReactNode
  colors?: any
  onClick?: (a: any) => void
  margin?: { top?: number; bottom?: number; right?: number; left?: number }
}

export default function StackedBar({
  layout,
  data,
  width,
  height,
  xdata,
  ydata,
  colors,
  tooltip,
  margin,
  onClick,
}: IStackedBarProps) {
  const skeleton = !data || !data.length
  const _data = skeleton ? noData : data

  let maxLen = layout === 'vertical' ? getMaxLabelLength(_data, ydata.key || '') : 0

  if (maxLen > 15) {
    maxLen = 15
  }

  let left = maxLen * 5

  if (left > width / 2) {
    maxLen = Math.round(width / (2 * 5))
    left = maxLen * 5
  }

  const bars = skeleton
    ? []
    : xdata.groups.map((dataKey: any, i: number) => ({
        dataKey,
        stackId: 'a',
        fill: `#${colors(i)}`,
      }))

  return (
    <BarChart
      style={{ fontSize: 12 }}
      layout={layout}
      width={width}
      height={height}
      data={_data}
      margin={{ ...margin, left }}
    >
      <CartesianGrid
        stroke="#d7dde1"
        vertical={layout === 'vertical'}
        horizontal={layout !== 'vertical'}
      />

      {xdata.key && (
        <XAxis
          axisLine={false}
          tickLine={false}
          type={layout === 'vertical' ? 'number' : 'category'}
          tick={{ fill: 'var(--grey1)' }}
          tickFormatter={(d) => prettyNumber({ value: d })}
          label={{
            value: xdata.label || xdata.key,
            position: 'insideBottom',
            fill: 'var(--grey1)',
            fontStyle: 'italic',
            offset: 0,
          }}
        />
      )}

      {ydata.key && (
        <YAxis
          dataKey={ydata.key}
          axisLine={false}
          tickLine={false}
          type={layout === 'vertical' ? 'category' : 'number'}
          interval={0}
          tick={layout === 'vertical' ? <CustomTick maxLen={maxLen} /> : { fill: 'var(--grey1)' }}
          label={{
            value: ydata.label || ydata.key,
            angle: -90,
            offset: -maxLen * 5 + 10,
            position: 'insideLeft',
            fill: 'var(--grey1)',
            fontStyle: 'italic',
          }}
        />
      )}

      {!skeleton && <Tooltip animationDuration={0} content={tooltip} />}

      {bars.map(
        (
          { dataKey, stackId, fill }: { dataKey: any; stackId: string; fill: string },
          i: number
        ) => (
          <Bar
            key={i}
            style={{ cursor: 'pointer' }}
            dataKey={dataKey}
            stackId={stackId}
            fill={fill}
            maxBarSize={40}
            isAnimationActive
            animationDuration={300}
            onClick={(d) => onClick && onClick(d.payload)}
          />
        )
      )}
      {skeleton && (
        <text
          fontSize={16}
          x={width / 2}
          y={height / 2}
          dy={-5}
          textAnchor="middle"
          fill="var(--grey1)"
        >
          No data
        </text>
      )}
    </BarChart>
  )
}

interface CustomTickProps {
  x?: number
  y?: number
  stroke?: string
  payload?: any
  maxLen: number
}

const CustomTick = ({ x, y, payload, maxLen }: CustomTickProps) => {
  const formattedValue = Number.isNaN(Number(payload.value))
    ? payload.value
    : prettyNumber({
        value: payload.value,
        numberType: NumberType.ROUNDED_NUMBER,
      })

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" fill="var(--grey1)">
        {formattedValue.length > maxLen + 1 && maxLen !== 0
          ? `${formattedValue.substr(0, maxLen)}...`
          : `${formattedValue}` || 'n/a'}
      </text>
    </g>
  )
}
